import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BaseModal from "../../modals/BaseModal";
import { ImageUploader } from "../../shared/ImageUploader";
import { Langs } from "../../../domain/enums/common/LangsEnum";
import { Tabs, Tab } from "@mui/material";
import { DEFAULT_LANGUAGE } from "../../../Config";
import { Owner } from "../../../domain/enums/owner-enum";
import { Purpose } from "../../../domain/enums/purpose-enum";
import { useFileEncoder } from "../../../custom-hooks/useFileEncoder";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import { IManagement, ManagementFormValues } from "../../../domain/models/Management";
import { managementFields, managementSchemaFactory } from "../../../validation-schemas/ManagementSchemas";
import CustomToggle from "../../../custom-components/form-inputs/CustomToggle";
import TranslationFieldsRenderer from "../../shared/form-inputs/TranslationFieldsRenderer";
import { isBoolean } from "lodash";

interface ManagementEditorProps {
    entity?: IManagement | null;
    onClose: () => void;
    onSave: (saved: IManagement) => void;
    translateFields: (
        translationFields: Record<string, string>,
        sourceLang: Langs,
        targetLang: Langs
    ) => Promise<Record<string, string>>;
}

const ManagementEditor: FC<ManagementEditorProps> = ({ entity, onClose, onSave, translateFields }) => {
    const { t } = useTranslation();
    const { encodeFiles } = useFileEncoder();
    const [selectedLang, setSelectedLang] = useState<Langs>(DEFAULT_LANGUAGE);
    const [existingImageUrl, setExistingImageUrl] = useState(entity?.pictureExists(Purpose.AVATAR) ? entity?.getPictureUrl() : null);
    const tabs = useMemo(() => Object.values(Langs), []);

    const translationsInitial = useMemo(() => {
        return Object.values(Langs).map((lang) => {
            const existing = entity?.translations?.find((t) => t?.languageCode === lang);
            return {
                languageCode: lang,
                managementId: entity?.id,
                fullName: existing?.fullName ?? "",
                position: existing?.position ?? "",
            };
        }) as ManagementFormValues["translations"];
    }, [entity?.translations]);

    const { form } = useCustomFormik<ManagementFormValues>({
        initial: {
            files: entity?.files,
            translations: translationsInitial,
            isActive: isBoolean(entity?.isActive) ? entity?.isActive : true,
        } as ManagementFormValues,
        fields: managementFields,
        schema: managementSchemaFactory(t),
        onSubmit: (values) => {
            const validLangs = values.translations.filter(
                (l) => l.fullName?.trim()?.length && l.position?.trim()?.length
            );

            const updated: IManagement = {
                ...entity,
                ...values,
                translations: validLangs,
            };
            onSave(updated);
            onClose();
        },
    });

    const isDefaultLangValid = useMemo(() => {
        const defaultTr = form.values.translations.find((t) => t.languageCode === DEFAULT_LANGUAGE);
        return !!(defaultTr && defaultTr.fullName?.trim().length > 1 && defaultTr.position?.trim().length > 1);
    }, [form.values.translations]);

    const handleFileChange = useCallback(
        async (file: File | null) => {
            if (!file) {
                form.setFieldValue("files", []);
                return;
            }
            const dataUrl = await encodeFiles(file);
            const files = [
                {
                    order: 1,
                    ownerType: Owner.MANAGEMENT,
                    purposeType: Purpose.AVATAR,
                    fileType: file.type.split("/")[1],
                    dataUrl,
                },
            ];
            form.setFieldValue("files", files);
        },
        [encodeFiles]
    );

    const handleTranslateEntity = async () => {
        const defaultTr = form.values.translations.find((t) => t.languageCode === DEFAULT_LANGUAGE);
        if (!defaultTr) return;

        const payload = {
            fullName: defaultTr.fullName ?? "",
            position: defaultTr.position ?? "",
        };

        try {
            const translated = await translateFields(payload, DEFAULT_LANGUAGE, selectedLang);
            // ensure translated has the expected keys
            const translatedTr = {
                languageCode: selectedLang,
                managementId: entity?.id,
                fullName: translated.fullName ?? "",
                position: translated.position ?? "",
            } as ManagementFormValues["translations"][number];

            const idx = form.values.translations.findIndex((t) => t.languageCode === selectedLang);
            if (idx >= 0) {
                form.setFieldValue(`translations[${idx}]`, translatedTr);
            } else {
                form.setFieldValue("translations", [...form.values.translations, translatedTr]);
            }
        } catch (err) {
            console.error("translate error", err);
        }
    };

    return (
        <BaseModal
            isVisible={true}
            setIsVisible={onClose}
            title={entity ? t("console.management.editManagement") : t("console.management.createManagement")}
            confirmTitle={t("console.common.save")}
            cancelTitle={t("console.common.cancel")}
            confirmHandler={form.handleSubmit}
            closeHandler={onClose}
            additionalWrapClassName="w-[90vw]"
            confirmButtonDisabled={!form.isValid}
        >
            <div className="flex gap-6 h-[70vh]">
                {/* or 0.4 */}
                <div className="flex flex-col gap-4 flex-[0.35]">
                    <div className="text-sm font-medium text-light-text-100 dark:text-dark-text-100">
                        {t("console.common.cover")}
                    </div>
                    <ImageUploader
                        imageUrl={existingImageUrl}
                        deleteImage={() => {
                            handleFileChange(null);
                            setExistingImageUrl(null);
                        }}
                        onChange={(file) => handleFileChange(file)}
                    />
                </div>

                <div className="flex flex-col flex-1 h-full overflow-auto">
                    <div className="flex flex-row justify-between">
                        <Tabs
                            value={selectedLang}
                            onChange={(_, newValue) => setSelectedLang(newValue as Langs)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {tabs.map((lang) => (
                                <Tab
                                    key={lang}
                                    value={lang}
                                    label={lang.toUpperCase()}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: "medium",
                                    }}
                                    className="dark:text-dark-text-100"
                                />
                            ))}
                        </Tabs>

                        {selectedLang !== DEFAULT_LANGUAGE && (
                            <div className="flex items-center">
                                <button
                                    className={`w-48 text-white font-medium rounded-lg text-sm px-5 py-2 text-center ${isDefaultLangValid
                                        ? "bg-blue-700 hover:bg-blue-800"
                                        : "bg-gray-400 cursor-not-allowed"
                                        }`}
                                    onClick={handleTranslateEntity}
                                    disabled={!isDefaultLangValid}
                                    title={
                                        (!isDefaultLangValid &&
                                            `${t(
                                                "console.common.fillDefaultLangFirst"
                                            )} ${DEFAULT_LANGUAGE.toUpperCase()}`) ||
                                        ""
                                    }
                                >
                                    {t("console.common.translateFromDefault")}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="overflow-auto">
                        <p className={`mt-1 ml-3 text-sm font-normal text-red-500`}>
                            {(typeof form.errors.translations === "string" && form.errors.translations) || "\u00A0"}
                        </p>

                        {form.values.translations.map((tr, index) => (
                            <div
                                className="overflow-auto"
                                key={tr.languageCode}
                                role="tabpanel"
                                hidden={selectedLang !== tr.languageCode}
                            >
                                <TranslationFieldsRenderer
                                    translation={tr}
                                    index={index}
                                    fieldConfigs={managementFields.translations.objectArray}
                                    errors={(form.errors.translations?.[index] as any) || undefined}
                                    t={t}
                                    setFieldValue={(fieldName, value) => form.setFieldValue(fieldName, value)}
                                    handleFieldChange={(fieldName) => form.handleChange(fieldName)}
                                />
                            </div>
                        ))}

                        <CustomToggle
                            labelKey={managementFields.isActive.placeholderKey}
                            name={"isActive"}
                            value={form.values.isActive}
                            onChange={form.handleChange("isActive")}
                            errorKey={form.errors.isActive}
                        />
                    </div>
                </div>
            </div>
        </BaseModal>
    );
};

export default ManagementEditor;
